/**
 * InvitePage (invite-coming-soon-page) — the client Invite nav tab, rebuilt as a
 * Warm Wellness-Luxe "Coming Soon" page (replaces the CWR 1.2 bare placeholder).
 * Every colour is an injected theme token (:root[data-client-theme]); NO
 * hardcoded theme hexes. Visual authority:
 * docs/mockups/client-dashboard-redesign/invite-coming-soon.html (Frame 3).
 */

/* ── Pinned header row (shipped Bookings/Profile pattern: glass back + title) ── */
.pageHeaderSection {
  position: relative;
  padding: 0 18px 10px;
}
.back {
  position: absolute;
  top: 0;
  left: 18px;
}
.pageTitleBlock {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 44px;
  padding: 0 64px; /* clears the 44px back button */
}
.pageTitle {
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-weight: 600;
  font-size: 25px;
  letter-spacing: -0.006em;
  line-height: 1.1;
  color: var(--text-primary);
}

/* ── Coming-soon wrap — optically centered in the shell's scrolling <main> ── */
.csWrap {
  min-height: calc(100% - 8px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 28px 56px; /* optical center: lift content a touch above true center */
  text-align: center;
}
@keyframes warmRise {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.csWrap > * {
  animation: warmRise 0.6s 0.1s cubic-bezier(0.16, 1, 0.3, 1) backwards;
}

/* ── Reusable glass card (tapered border) — shipped .gcard recipe ── */
.gcard {
  position: relative;
  background: var(--glass-bg-strong);
  backdrop-filter: var(--glass-blur);
  box-shadow: var(--glass-shadow);
  border-radius: 18px;
  overflow: hidden;
}
.gcard::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(135deg, rgba(var(--accent-light-rgb), 0.16) 0%, rgba(var(--accent-light-rgb), 0.03) 100%);
  -webkit-mask: linear-gradient(black 0 0) content-box, linear-gradient(black 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
  z-index: 2;
}
.gcard > * {
  position: relative;
  z-index: 1;
}

/* ── The invitation card (Frame 3 "The invitation") ── */
.invitation {
  width: 100%;
  padding: 40px 28px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.invitationInner {
  position: absolute;
  inset: 10px;
  border: 1px solid rgba(var(--accent-light-rgb), 0.18);
  border-radius: 12px;
  pointer-events: none;
}
.invKicker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: var(--accent);
}
.invTitle {
  margin-top: 16px;
  font-family: var(--client-font-heading, 'EB Garamond', Georgia, serif);
  font-style: italic;
  font-weight: 500;
  font-size: 31px;
  letter-spacing: -0.005em;
  line-height: 1.15;
  color: var(--text-primary);
}
.invDivider {
  margin: 20px auto 0;
  display: flex;
  align-items: center;
  gap: 10px;
  color: rgba(var(--accent-rgb), 0.55);
}
.invDivider::before,
.invDivider::after {
  content: '';
  width: 44px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(var(--accent-rgb), 0.4));
}
.invDivider::after {
  background: linear-gradient(90deg, rgba(var(--accent-rgb), 0.4), transparent);
}
.invDivider svg {
  width: 9px;
  height: 9px;
}
.invBody {
  margin-top: 18px;
  font-size: 13.5px;
  color: var(--text-secondary);
  line-height: 1.65;
  max-width: 250px;
}
.invFoot {
  margin-top: 22px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-tertiary);
}
